/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * IndexedDB database name.
 * All client-side storage lives in this single database.
 */
export const DB_NAME: string = "appCache";

/**
 * IndexedDB schema version.
 * Increment when the object-store layout changes (triggers the upgrade callback).
 */
export const DB_VERSION: number = 1;

/**
 * Name of the single object store inside the database.
 * All records — plain and checksummed — are stored here, keyed by a logical name.
 */
export const STORE_NAME: string = "cache";

/**
 * Name of the IndexedDB index on the validTill field.
 * Used by the startup cleanup pass to efficiently locate expired records.
 */
export const VALID_TILL_INDEX: string = "validTill";

/**
 * Name of the IndexedDB index on the evictTill field.
 * Used by the startup cleanup pass to efficiently locate records to evict.
 */
export const EVICT_TILL_INDEX: string = "evictTill";

/**
 * Shape of a cache-key registry entry.
 * Indicates whether the corresponding record requires SHA-256 checksum protection.
 */
interface TCacheKeyConfig {
  /**
   * When true, a SHA-256 checksum is computed at write time and verified at read time.
   */
  checksummed: boolean;
}

/**
 * Centralised storage key constants.
 * Use these instead of hardcoded string literals throughout the codebase.
 */
export const STORAGE_KEY_ACCESS_TOKEN_WITH_EXPIRY: string =
  "accessTokenWithExpiry";
export const STORAGE_KEY_IDP_JWT_TOKEN: string = "idpJwtToken";
export const STORAGE_KEY_BODY_ID: string = "bodyId";
export const STORAGE_KEY_QUICK_MENU_SHORTCUT: string = "QUICK_MENU_SHORTCUT";
export const STORAGE_KEY_QUICK_MENU_SHORTCUT_LANGUAGE: string =
  "quickMenuShortcutLanguage";
export const STORAGE_KEY_LAST_APP_VERSION: string = "lastAppVersion";
export const STORAGE_KEY_LANGUAGE: string = "language";
export const STORAGE_KEY_USER_PREFERRED_AUDIO: string = "USER_PREFERRED_AUDIO";
export const STORAGE_KEY_USER_PREFERRED_AUDIO_DESCRIPTION: string =
  "USER_PREFERRED_AUDIO_DESCRIPTION";
export const STORAGE_KEY_USER_PREFERRED_CC: string = "USER_PREFERRED_CC";
export const STORAGE_KEY_IS_TEXT_TRACK_ENABLED: string =
  "IS_TEXT_TRACK_ENABLED";
export const STORAGE_KEY_SOFT_CLIENT_CERT: string = "softClientCert";
export const STORAGE_KEY_SOFT_CLIENT_CERT_KEY: string = "softClientCertKey";
export const STORAGE_KEY_LAST_TUNED_CHANNEL: string = "lastTunedChannel";
export const STORAGE_KEY_PARTNER_ID: string = "partnerId";
export const STORAGE_KEY_PARTNER_CUSTOMER_ID: string = "partnerCustomerId";
export const STORAGE_KEY_DEVICE_UUID: string = "deviceUuid";
export const STORAGE_KEY_PARTNER_LOGO_URL: string = "partnerLogoUrl";
export const STORAGE_KEY_OVERRIDE_DEVICE_TYPE: string = "overrideDeviceType";
export const STORAGE_KEY_OVERRIDE_IP_AUTOLOGIN: string = "overrideIpAutologin";
export const STORAGE_KEY_ENABLE_IP_AUTOLOGIN: string = "enableIpAutologin";
export const STORAGE_KEY_ACTIVE_PROFILE_SELECTION: string =
  "activeProfileSelection";
export const STORAGE_KEY_PROFILE_DOMAIN_TOKEN_PREFIX: string =
  "profileDomainToken:";
export const STORAGE_KEY_IP_JWT_TOKEN: string = "ipJwtToken";
export const STORAGE_KEY_LAST_AUTH_CONTEXT: string = "lastAuthContext";
export const STORAGE_KEY_WHINY_WINDOW: string = "whinyWindow";
export const STORAGE_KEY_SERVICE_API_LOGGING_WINDOW: string =
  "serviceApiLoggingWindow";
/**
 * Persists the full multi-select guide filter set (Favorites and/or one or
 * more categories) — see `toggleGuideFilter`/`restoreGuideFilterSelection`
 * in guideSlice.ts.
 */
export const STORAGE_KEY_GUIDE_SELECTED_FILTER_IDS: string =
  "guideSelectedFilterIds";
/**
 * @deprecated Written by builds before operational/analytics events were also
 * queued. {@link PendingTelemetryStore} drains it once on first read, then
 * deletes it. Do not write to it.
 */
export const STORAGE_KEY_PENDING_DIAGNOSTIC_LOGS: string =
  "pendingDiagnosticLogs";

export const STORAGE_KEY_PENDING_TELEMETRY_LOGS: string =
  "pendingTelemetryLogs";

/**
 * Registry of all known cache keys and their checksum requirements.
 *
 * - **Plain** keys (checksummed: false): non-sensitive cached data — channels, EPG,
 *   API responses, preferences, UI state.
 * - **Checksummed** keys (checksummed: true): sensitive data — tokens, entitlements,
 *   device provisioning, feature flags.
 *
 * Add new keys here as API integration progresses.
 */
export const CACHE_KEY_REGISTRY: Readonly<Record<string, TCacheKeyConfig>> = {
  /** User preferences, UI state, app metadata — plain, non-sensitive. */
  preferences: { checksummed: false },

  /** Full channel catalogue — plain, large payload. */
  channels: { checksummed: false },

  /** Per-day EPG entries (actual keys are epg_YYYY_MM_DD) — plain, large payload. */
  epg: { checksummed: false },

  /** Content discovery, search results, VOD metadata — plain, server-driven TTL. */
  apiCache: { checksummed: false },

  /** Access token, refresh token — checksummed to detect corruption. */
  tokens: { checksummed: true },

  /** Access token + expiry persisted by DeviceModel — checksummed to detect corruption. */
  accessTokenWithExpiry: { checksummed: true },

  /** IDP JWT token persisted by DeviceModel for silent domain-token refresh — checksummed. */
  idpJwtToken: { checksummed: true },

  /** Body ID (device provisioning) — checksummed to detect corruption. */
  bodyId: { checksummed: true },

  /** User subscription / profile data — checksummed to detect corruption. */
  entitlements: { checksummed: true },

  /** caDeviceId, token expiry, feature flags — checksummed to detect corruption. */
  metadata: { checksummed: true },

  /** Device Login ID (per-device) — checksummed to detect corruption. */
  dlid: { checksummed: true },

  /** DLID cryptographic keys and signature — checksummed to detect corruption. */
  dlid_keys: { checksummed: true },

  /** Soft client X.509 certificate with bodyId and validity timestamps — checksummed to detect corruption. */
  softClientCert: { checksummed: true },

  /** Soft client certificate private key — checksummed to detect corruption. */
  softClientCertKey: { checksummed: true },

  /** Quick menu shortcuts cached by domain-lib HomeShortcutService — plain, non-sensitive. */
  QUICK_MENU_SHORTCUT: { checksummed: false },

  /** Language string cached alongside quick menu shortcuts for language-invalidation. */
  quickMenuShortcutLanguage: { checksummed: false },

  /** Last recorded app version — used to detect updates and bypass the menu cache. */
  lastAppVersion: { checksummed: false },

  /** Current app language persisted at launch as a user preference. */
  language: { checksummed: false },

  /** Preferred player audio selection (language + codec) for user-selected tracks. */
  USER_PREFERRED_AUDIO: { checksummed: false },

  /** Preferred player closed-caption selection (language + kind) for user-selected tracks. */
  USER_PREFERRED_CC: { checksummed: false },

  /** Whether closed captions / text tracks are enabled — plain, user preference. */
  IS_TEXT_TRACK_ENABLED: { checksummed: false },

  /** Last tuned live channel number — plain, user preference. */
  lastTunedChannel: { checksummed: false },

  /** Persisted profile selection and skip preference — plain, user preference. */
  activeProfileSelection: { checksummed: false },

  /** Profile-scoped domain tokens use dynamic body/profile ID suffixes — checksummed credentials. */
  [STORAGE_KEY_PROFILE_DOMAIN_TOKEN_PREFIX]: { checksummed: true },

  /** MSO partner ID from provisionDevice — checksummed to detect corruption. */
  partnerId: { checksummed: true },

  /** Partner customer ID from provisionDevice — checksummed to detect corruption. */
  partnerCustomerId: { checksummed: true },

  /** Device UUID (per install/device identity) — checksummed to detect corruption. */
  deviceUuid: { checksummed: true },

  /** Partner branding logo URL resolved from the branding UI bundle — plain, UI state. */
  partnerLogoUrl: { checksummed: false },

  /** IP auto-login JWT token persisted by DeviceModel — checksummed credential. */
  ipJwtToken: { checksummed: true },

  /** Last authentication context (type + URL + schemes) for token refresh routing — checksummed. */
  lastAuthContext: { checksummed: true },

  /** Persisted Whiny logging window (epoch-second bounds) — plain, non-PII runtime config. */
  whinyWindow: { checksummed: false },

  /** Persisted Service-API logging window (epoch-second bounds) — plain, non-PII runtime config. */
  serviceApiLoggingWindow: { checksummed: false },

  /** Guide multi-select filter selection (Favorites + categories) — plain, user preference. */
  guideSelectedFilterIds: { checksummed: false },

  /** Diagnostic logs queued before Sage identity is available — plain, non-sensitive. */
  pendingDiagnosticLogs: { checksummed: false },

  /** Telemetry queued before Sage identity is available — plain, non-sensitive. */
  pendingTelemetryLogs: { checksummed: false }
};

/**
 * Determine whether a given cache key requires checksum protection.
 *
 * For EPG keys (formatted as `epg_YYYY_MM_DD`), the lookup falls back to the
 * base `epg` entry in the registry.
 *
 * @param key - The cache key to look up (e.g. "tokens", "epg_2026_03_16")
 * @returns true if the key is classified as checksummed; false otherwise
 */
export function isChecksummed(key: string): boolean {
  const registryEntry = CACHE_KEY_REGISTRY[key];
  if (registryEntry !== null && registryEntry !== undefined) {
    return registryEntry.checksummed;
  }

  // EPG keys are date-suffixed (e.g. "epg_2026_03_16") — fall back to the base "epg" entry.
  if (key.startsWith("epg_")) {
    const baseEntry = CACHE_KEY_REGISTRY["epg"];
    if (baseEntry !== null && baseEntry !== undefined) {
      return baseEntry.checksummed;
    }
  }

  if (key.startsWith(STORAGE_KEY_PROFILE_DOMAIN_TOKEN_PREFIX)) {
    const baseEntry =
      CACHE_KEY_REGISTRY[STORAGE_KEY_PROFILE_DOMAIN_TOKEN_PREFIX];
    if (baseEntry !== null && baseEntry !== undefined) {
      return baseEntry.checksummed;
    }
  }

  return false;
}
